"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { site } from "@/content/site";
import { buildReservationWhatsAppUrl, type ReservationInput } from "@/lib/whatsapp";
import { getDefaultReservationDate, getTodayIsoDate } from "@/lib/reservation-date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const TIME_SLOTS = ["19:30", "20:00", "20:30", "21:00", "21:30"];

function guestOptions(it: boolean) {
  const counts = [2, 3, 4, 5, 6];
  const labels = counts.map((n) => (it ? `${n} persone` : `${n} people`));
  return [...labels, it ? "più di sei — la chiamiamo noi" : "more than six — we'll call you"];
}

interface ReservationCardProps {
  standalone?: boolean;
  onSubmit?: (form: ReservationInput) => void;
  className?: string;
}

export function ReservationCard({ standalone = false, onSubmit, className }: ReservationCardProps) {
  const locale = useLocale();
  const it = locale !== "en";
  const t = useTranslations("reservation");
  const guests = guestOptions(it);

  const [form, setForm] = useState<ReservationInput>({
    name: "",
    date: getDefaultReservationDate(),
    time: "20:30",
    guests: guests[0],
    notes: "",
  });

  const update = <K extends keyof ReservationInput>(key: K, value: ReservationInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const url = buildReservationWhatsAppUrl(site.whatsappNumber, form, it ? "it" : "en");
    window.open(url, "_blank", "noopener,noreferrer");
    onSubmit?.(form);
  };

  return (
    <form
      className={cn(
        // text-inchiostro esplicito: la card compare anche dentro sezioni a sfondo scuro
        // (fascia prenota, verde profondo) — senza questo il testo eredita il colore chiaro
        // del genitore e diventa illeggibile sul fondo chiaro della card stessa.
        "rounded-sm border border-pietra-light/40 bg-bianco-carta p-9 text-inchiostro shadow-[0_16px_40px_rgba(0,0,0,0.22)]",
        standalone && "mx-auto max-w-[560px]",
        className
      )}
      onSubmit={handleSubmit}
    >
      {/* Su /prenota (standalone) il titolo duplicherebbe alla lettera quello della
          pagina (SectionHead ha lo stesso testo) e salterebbe da h1 a h3 — nascosto
          lì, resta solo nella fascia prenotazione (ReservationStrip) dove la card è
          affiancata a un titolo diverso. Vedi decisions.md, audit Lighthouse. */}
      {!standalone && (
        <>
          <span className="eyebrow mb-1.5 block">{t("eyebrow")}</span>
          <h3 className="mb-5 font-display text-[30px] tracking-[-0.01em]">{t("title")}</h3>
        </>
      )}

      <div className="mb-4 flex flex-col gap-1.5">
        <Label htmlFor="reservation-name">{t("nameLabel")}</Label>
        <Input
          id="reservation-name"
          value={form.name}
          placeholder={t("namePlaceholder")}
          onChange={(e) => update("name", e.target.value)}
        />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3.5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reservation-date">{t("dateLabel")}</Label>
          <Input
            id="reservation-date"
            type="date"
            min={getTodayIsoDate()}
            value={form.date}
            onChange={(e) => update("date", e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="reservation-time">{t("timeLabel")}</Label>
          <select
            id="reservation-time"
            value={form.time}
            onChange={(e) => update("time", e.target.value)}
            className="h-9 rounded-sm border border-input bg-background px-3 text-sm outline-none focus-visible:border-rosso-pozzo"
          >
            {TIME_SLOTS.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-1.5">
        <Label htmlFor="reservation-guests">{t("guestsLabel")}</Label>
        <select
          id="reservation-guests"
          value={form.guests}
          onChange={(e) => update("guests", e.target.value)}
          className="h-9 rounded-sm border border-input bg-background px-3 text-sm outline-none focus-visible:border-rosso-pozzo"
        >
          {guests.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4 flex flex-col gap-1.5">
        <Label htmlFor="reservation-notes">
          {t("notesLabel")} <span className="font-display text-[13px] text-pietra italic">{t("notesHint")}</span>
        </Label>
        <Textarea
          id="reservation-notes"
          value={form.notes}
          placeholder={t("notesPlaceholder")}
          onChange={(e) => update("notes", e.target.value)}
          rows={3}
        />
      </div>

      <Button type="submit" size="lg" className="w-full sm:w-auto">
        {t("submit")}
      </Button>
      <p className="mt-3.5 font-display text-sm text-pietra italic">{t("waHint")}</p>
    </form>
  );
}
